import { Dispatch, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import {
  defaultSelectionFilter,
  importReviewReducer,
  ImportReviewAction,
  ImportReviewState,
  initialImportReviewState,
  isNotFoundError,
  itemsWithId,
  reviewFilterOf,
  selectedAmount,
} from '../../../functions/ProviderConsumptionImport.Review.Functions';
import {
  ImportDuplicateStatus,
  ImportPreview,
  ImportPreviewItem,
  PREVIEW_ITEMS_DEFAULT_PAGE_SIZE,
  PreviewItemIds,
  PreviewItemsFilter,
  PreviewItemsPage,
  PreviewItemsQuery,
} from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';

export interface UseImportSelectionOptions {
  /** Vista previa con `previewId` (sin `previewId` el hook no hace nada). */
  preview: ImportPreview | undefined;
  /** El paso "Revisar ítems" está visible: carga la selección inicial (una vez por vista previa) y las páginas. */
  active: boolean;
  loadItems?: (previewId: string, query: PreviewItemsQuery) => Promise<PreviewItemsPage>;
  loadItemIds?: (previewId: string, filter?: PreviewItemsFilter) => Promise<PreviewItemIds>;
  /** 404 de `items` / `item-ids`: la vista previa venció o se reemplazó (el asistente la regenera). */
  onPreviewGone?: (error: unknown) => void;
  /** `item-ids` inicial no coincide con `summary.defaultSelected` (otra pestaña reemplazó la vista previa). */
  onStale?: () => void;
}

export interface ImportSelection {
  previewId?: string;
  state: ImportReviewState;
  dispatch: Dispatch<ImportReviewAction>;
  /** La selección inicial ya llegó (`item-ids`). */
  ready: boolean;
  initializing: boolean;
  initError?: unknown;
  pageItems: ImportPreviewItem[];
  totalRecords: number;
  pageLoading: boolean;
  pageError?: unknown;
  /** "Todos los filtrados" en curso / con error. */
  selectingAll: boolean;
  selectAllError?: unknown;
  count: number;
  amount: number;
  toggle: (itemId: string) => void;
  selectPage: () => void;
  deselectPage: () => void;
  selectAllFiltered: () => Promise<void>;
  selectNone: () => void;
  setStatuses: (statuses: ImportDuplicateStatus[]) => void;
  setSearch: (search: string) => void;
  setPage: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
  /** Vuelve a pedir lo que falló (selección inicial o página). */
  retry: () => void;
  /** Vuelve a leer la página actual del servidor (sugerencias recalculadas). */
  reloadPage: () => void;
  /**
   * Ids de todos los ítems seleccionables que cumplen un filtro propio (misma sugerencia o misma clave de servicio), en
   * todas las páginas, con `item-ids`. Un 404 regenera la vista previa y devuelve undefined; otros errores se rechazan.
   */
  idsMatching: (filter: PreviewItemsFilter) => Promise<string[] | undefined>;
}

const isDefaultQuery = (state: ImportReviewState): boolean =>
  state.page.pageNumber === 1 &&
  state.page.pageSize === PREVIEW_ITEMS_DEFAULT_PAGE_SIZE &&
  state.filters.statuses.length === 0 &&
  !state.filters.search.trim();

/**
 * Selección del paso "Revisar ítems" (spec 009): reducer local, selección inicial con `item-ids?duplicateStatus=1&3`
 * (con importes por ítem), página del servidor (la primera puede salir de la vista previa) e importe seleccionado exacto
 * sin recorrer páginas. Descarta respuestas viejas (número de solicitud) y avisa los 404.
 */
export const useImportSelection = (options: UseImportSelectionOptions): ImportSelection => {
  const { preview, active, loadItems, loadItemIds } = options;
  const previewId = preview?.previewId ?? undefined;
  const [state, dispatch] = useReducer(importReviewReducer, undefined, () => initialImportReviewState());

  const [ready, setReady] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [initError, setInitError] = useState<unknown>(undefined);
  const [pageItems, setPageItems] = useState<ImportPreviewItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<unknown>(undefined);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectAllError, setSelectAllError] = useState<unknown>(undefined);
  const [reloadToken, setReloadToken] = useState(0);
  const [initToken, setInitToken] = useState(0);

  const initializedFor = useRef<string | undefined>(undefined);
  const initRequest = useRef(0);
  const pageRequest = useRef(0);

  // Callbacks del asistente en refs: cambian en cada render y no deben disparar recargas.
  const callbacks = useRef(options);
  callbacks.current = options;

  const gone = (error: unknown): boolean => {
    if (!isNotFoundError(error)) return false;
    callbacks.current.onPreviewGone?.(error);
    return true;
  };

  // ---- selección inicial: una vez por vista previa, al entrar al paso ----
  useEffect(() => {
    if (!active || !previewId || !loadItemIds) return;
    if (initializedFor.current === previewId) return;
    initializedFor.current = previewId;
    const requestId = ++initRequest.current;
    dispatch({ type: 'init', previewId, items: [] });
    setReady(false);
    setInitError(undefined);
    setSelectAllError(undefined);
    setInitializing(true);
    loadItemIds(previewId, defaultSelectionFilter())
      .then((result) => {
        if (requestId !== initRequest.current) return;
        dispatch({ type: 'init', previewId, items: result.items });
        setReady(true);
        const expected = preview?.summary?.defaultSelected;
        if (expected !== undefined && expected !== null && result.items.length !== expected) callbacks.current.onStale?.();
      })
      .catch((error: unknown) => {
        if (requestId !== initRequest.current) return;
        initializedFor.current = undefined;
        if (!gone(error)) setInitError(error);
      })
      .finally(() => {
        if (requestId === initRequest.current) setInitializing(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, previewId, initToken]);

  // ---- página: de la vista previa (primera, sin filtros) o del servidor ----
  const statusesKey = state.filters.statuses.join(',');
  const pageKeyReady = !!previewId && state.previewId === previewId;
  useEffect(() => {
    if (!active || !previewId || !pageKeyReady) return;
    const requestId = ++pageRequest.current;
    setPageError(undefined);
    const firstPage = itemsWithId(preview?.items);
    if (reloadToken === 0 && isDefaultQuery(state) && firstPage.length > 0) {
      setPageItems(firstPage);
      setTotalRecords(Number(preview?.summary?.total ?? preview?.totalRows ?? firstPage.length));
      setPageLoading(false);
      dispatch({ type: 'pageLoaded', items: firstPage });
      return;
    }
    if (!loadItems) return;
    setPageLoading(true);
    const filter = reviewFilterOf(state.filters);
    loadItems(previewId, { ...filter, pageNumber: state.page.pageNumber, pageSize: state.page.pageSize })
      .then((page) => {
        if (requestId !== pageRequest.current) return;
        setPageItems(page.items);
        setTotalRecords(page.totalRecords);
        dispatch({ type: 'pageLoaded', items: page.items });
      })
      .catch((error: unknown) => {
        if (requestId !== pageRequest.current) return;
        if (!gone(error)) setPageError(error);
      })
      .finally(() => {
        if (requestId === pageRequest.current) setPageLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, previewId, pageKeyReady, state.page.pageNumber, state.page.pageSize, statusesKey, state.filters.search, reloadToken]);

  // Vista previa nueva: se olvida la página anterior.
  useEffect(() => {
    pageRequest.current++;
    setPageItems([]);
    setTotalRecords(0);
    setPageError(undefined);
    setReloadToken(0);
    if (!previewId) {
      initializedFor.current = undefined;
      setReady(false);
    }
  }, [previewId]);

  const selectAllFiltered = useCallback(async () => {
    if (!previewId || !loadItemIds) return;
    setSelectingAll(true);
    setSelectAllError(undefined);
    try {
      const result = await loadItemIds(previewId, reviewFilterOf(state.filters));
      dispatch({ type: 'selectIds', items: result.items });
    } catch (error: unknown) {
      if (!gone(error)) setSelectAllError(error);
    } finally {
      setSelectingAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewId, loadItemIds, state.filters]);

  const amount = useMemo(() => selectedAmount(state), [state]);

  return {
    previewId,
    state,
    dispatch,
    ready,
    initializing,
    initError,
    pageItems,
    totalRecords,
    pageLoading,
    pageError,
    selectingAll,
    selectAllError,
    count: state.selected.size,
    amount,
    toggle: (itemId) => dispatch({ type: 'toggle', itemId }),
    selectPage: () => dispatch({ type: 'selectPage', items: pageItems }),
    deselectPage: () => dispatch({ type: 'deselectPage', items: pageItems }),
    selectAllFiltered,
    selectNone: () => dispatch({ type: 'selectNone' }),
    setStatuses: (statuses) => dispatch({ type: 'setFilters', statuses }),
    setSearch: (search) => dispatch({ type: 'setFilters', search }),
    setPage: (pageNumber) => dispatch({ type: 'setPage', pageNumber }),
    setPageSize: (pageSize) => dispatch({ type: 'setPage', pageNumber: 1, pageSize }),
    retry: () => {
      if (initError !== undefined) {
        initializedFor.current = undefined;
        setInitToken((value) => value + 1);
      }
      if (pageError !== undefined) setReloadToken((value) => value + 1);
    },
    reloadPage: () => setReloadToken((value) => value + 1),
    idsMatching: async (filter) => {
      if (!previewId || !loadItemIds) return [];
      try {
        const result = await loadItemIds(previewId, filter);
        return result.items.map((item) => String(item.id));
      } catch (error: unknown) {
        if (gone(error)) return undefined;
        throw error;
      }
    },
  };
};

export default useImportSelection;
